import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FwTooltip, FwModal, FwButton, FwSelect, FwSelectOption, FwModalTitle, FwModalContent, FwModalFooter, FwInput } from "@freshworks/crayons/react";
import { formatOrderItemAmount } from '../utils/FormatHelper';
import iconCreateOrder from '../../assets/icon_create_order.svg';

const CreateOrderModal = ({ order, onCreateOrder, client }) => {
    const { t } = useTranslation();
    const modalRef = useRef();
    const modalContentRef = useRef();

    const [selectedOrderType, setSelectedOrderType] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [returnReasons, setReturnReasons] = useState([]);
    const [creditNoteReasons, setCreditNoteReasons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [includeShippingCosts, setIncludeShippingCosts] = useState(false);
    const [includeReturnCosts, setIncludeReturnCosts] = useState(false);
    const [includeInternationalReturnCosts, setIncludeInternationalReturnCosts] = useState(false);
    const [returnCostsConfig, setReturnCostsConfig] = useState(null);

    // Resolve the applicable return cost from a bracket configuration for a given order value.
    const resolveReturnCostFromBrackets = (brackets, orderValue) => {
        if (!Array.isArray(brackets) || brackets.length === 0) return null;

        const match = brackets.find(bracket => {
            const min = bracket.min_order_value ?? 0;
            const max = bracket.max_order_value;
            const aboveMin = orderValue >= min;
            const belowMax = max === null || max === undefined ? true : orderValue <= max;
            return aboveMin && belowMax;
        });

        return match ? match.cost : null;
    };

    const getOrderGrossTotal = () => {
        const systemCurrencyAmount = order.amounts?.find(amount => amount.isSystemCurrency);
        return systemCurrencyAmount?.grossTotal || 0;
    };

    const getReturnCostsValue = () => {
        if (!returnCostsConfig) return null;
        const grossTotal = getOrderGrossTotal();

        if (includeReturnCosts) {
            return resolveReturnCostFromBrackets(returnCostsConfig.national_return_costs, grossTotal);
        }
        if (includeInternationalReturnCosts) {
            return resolveReturnCostFromBrackets(returnCostsConfig.international_return_costs, grossTotal);
        }
        return null;
    };

    // Define available order types based on the current order type
    const getAvailableOrderTypes = () => {
        switch (order.typeId) {
            case 1: // Sales Orders
                return [
                    { id: 3, name: t('create_return'), endpoint: 'returns' },
                    { id: 5, name: t('create_warranty'), endpoint: 'warranties' },
                    { id: 4, name: t('create_credit_note'), endpoint: 'credit_notes' }
                ];
            case 3: // Returns
                return [
                    { id: 5, name: t('create_warranty'), endpoint: 'warranties' },
                    { id: 4, name: t('create_credit_note'), endpoint: 'credit_notes' },
                    // { id: 6, name: t('create_repair'), endpoint: 'repairs' }
                ];
            case 5: // Warranties
                return [
                    { id: 3, name: t('create_return'), endpoint: 'returns' },
                    { id: 4, name: t('create_credit_note'), endpoint: 'credit_notes' }
                ];
            default:
                return [];
        }
    };

    const availableOrderTypes = getAvailableOrderTypes();

    // Filter out shipping cost items (typeId 6)
    const orderItems = order.orderItems.filter(item => item.typeId !== 6);

    const handleOpenModal = () => {
        modalRef.current.open();
        setSelectedOrderType('');
        setSelectedItems([]);
        setIncludeShippingCosts(false);
        setIncludeReturnCosts(false);
        setIncludeInternationalReturnCosts(false);
        loadReasons();
    };

    const handleCloseModal = () => {
        if (modalRef.current) {
            modalRef.current.close();
        }
        setSelectedOrderType('');
        setSelectedItems([]);
        setIncludeShippingCosts(false);
        setIncludeReturnCosts(false);
        setIncludeInternationalReturnCosts(false);
    };

    const loadReasons = async () => {
        try {
            // Load return reasons
            const returnReasonsResponse = await client.request.invoke('getReturnReasons', {});
            setReturnReasons(returnReasonsResponse.response);
        } catch (error) {
            console.error('Error loading return reasons:', error);
            // Continue without return reasons if the API fails
            setReturnReasons([]);
        }

        try {
            // Load credit note reasons
            const creditNoteReasonsResponse = await client.request.invoke('getCreditNoteReasons', {});
            setCreditNoteReasons(creditNoteReasonsResponse.response);
        } catch (error) {
            console.error('Error loading credit note reasons:', error);
            // Continue without credit note reasons if the API fails
            setCreditNoteReasons([]);
        }

        try {
            const zafSettings = await client.zafClient.metadata();
            const rawReturnCosts = zafSettings?.settings?.fieldReturnCosts;
            if (rawReturnCosts) {
                const parsed = typeof rawReturnCosts === 'string'
                    ? JSON.parse(rawReturnCosts)
                    : rawReturnCosts;
                setReturnCostsConfig(parsed);
            } else {
                setReturnCostsConfig(null);
            }
        } catch (error) {
            console.error('Error loading fieldReturnCosts setting:', error);
            setReturnCostsConfig(null);
        }
    };

    const handleOrderTypeChange = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setSelectedOrderType(e.detail.value);
        setIncludeShippingCosts(false);
        setIncludeReturnCosts(false);
        setIncludeInternationalReturnCosts(false);
        // Reset selected items when order type changes
        setSelectedItems(orderItems.map(item => ({
            orderItemId: item.id,
            orderItemName: item.orderItemName,
            quantity: 0,
            maxQuantity: item.quantity,
            originalPrice: item.amounts?.find(amount => amount.isSystemCurrency)?.priceGross || 0,
            customPrice: item.amounts?.find(amount => amount.isSystemCurrency)?.priceGross || 0,
            returnReasonId: returnReasons.length > 0 ? returnReasons[0].id : 1,
            creditNoteReasonId: creditNoteReasons.length > 0 ? creditNoteReasons[0].id : 1,
            remainingPercentage: 100
        })));
    };

    const handleItemQuantityChange = (itemId, quantity) => {
        setSelectedItems(prevItems =>
            prevItems.map(item =>
                item.orderItemId === itemId
                    ? { ...item, quantity: Math.max(0, Math.min(quantity, item.maxQuantity)) }
                    : item
            )
        );
    };

    const handleItemPriceChange = (itemId, price) => {
        setSelectedItems(prevItems =>
            prevItems.map(item =>
                item.orderItemId === itemId
                    ? { ...item, customPrice: Math.max(0, price) }
                    : item
            )
        );
    };

    const handleReturnReasonChange = (e, itemId) => {
        e.stopPropagation();
        e.preventDefault();
        setSelectedItems(prevItems =>
            prevItems.map(item =>
                item.orderItemId === itemId
                    ? { ...item, returnReasonId: parseInt(e.detail.value) }
                    : item
            )
        );
    };

    const handleCreditNoteReasonChange = (e, itemId) => {
        e.stopPropagation();
        e.preventDefault();
        setSelectedItems(prevItems =>
            prevItems.map(item =>
                item.orderItemId === itemId
                    ? { ...item, creditNoteReasonId: parseInt(e.detail.value) }
                    : item
            )
        );
    };

    const handleRemainingPercentageChange = (itemId, percentage) => {
        setSelectedItems(prevItems =>
            prevItems.map(item =>
                item.orderItemId === itemId
                    ? { ...item, remainingPercentage: Math.max(0, Math.min(100, percentage)) }
                    : item
            )
        );
    };

    const handleSubmit = async () => {
        if (!selectedOrderType) {
            await client.interface.trigger("showNotify", { type: "danger", message: t("please_select_order_type") });
            return;
        }

        const itemsToCreate = selectedItems.filter(item => item.quantity > 0);
        
        if (itemsToCreate.length === 0) {
            await client.interface.trigger("showNotify", { type: "danger", message: t("please_select_items") });
            return;
        }

        setLoading(true);

        try {
            // Prepare the quantities array based on order type
            let quantities = [];
            
            if (selectedOrderType === '3') { // Returns
                quantities = itemsToCreate.map(item => ({
                    orderItemId: item.orderItemId,
                    quantity: item.quantity,
                    orderItemName: item.orderItemName,
                    returnReasonId: item.returnReasonId,
                    remainingPercentage: item.remainingPercentage
                }));
            } else if (selectedOrderType === '5') { // Warranties
                quantities = itemsToCreate.map(item => ({
                    orderItemId: item.orderItemId,
                    quantity: item.quantity,
                    orderItemName: item.orderItemName
                }));
            } else if (selectedOrderType === '6') { // Repairs
                quantities = itemsToCreate.map(item => ({
                    orderItemId: item.orderItemId,
                    quantity: item.quantity,
                    orderItemName: item.orderItemName
                }));
            } else if (selectedOrderType === '4') { // Credit Notes
                quantities = itemsToCreate.map(item => ({
                    orderItemId: item.orderItemId,
                    quantity: item.quantity,
                    orderItemName: item.orderItemName,
                    priceOriginalGrossOrderCurrency: item.customPrice,
                    creditNoteReasonId: item.creditNoteReasonId
                }));
            }

            // Call the appropriate API endpoint
            let actionName = '';
            if (selectedOrderType === '3') {
                actionName = 'createReturn';
            } else if (selectedOrderType === '5') {
                actionName = 'createWarranty';
            } else if (selectedOrderType === '4') {
                actionName = 'createCreditNote';
            } else if (selectedOrderType === '6') {
                actionName = 'createRepair';
            }

            const requestData = {
                orderId: order.id,
                quantities: quantities
            };

            // Add shipping costs flag and return costs flag for credit notes
            if (selectedOrderType === '4') {
                requestData.includeShippingCosts = includeShippingCosts;
                requestData.includeReturnCosts = includeReturnCosts;
                requestData.includeInternationalReturnCosts = includeInternationalReturnCosts;
                
                // Add shipping costs value when including shipping costs
                if (includeShippingCosts) {
                    // Get shipping cost from order.amounts where isSystemCurrency is true
                    const systemCurrencyAmount = order.amounts?.find(amount => amount.isSystemCurrency);
                    const shippingCostsValue = systemCurrencyAmount?.shippingCostsGross || 0;
                    console.log("DEBUG - SHIPPING COSTS VALUE", shippingCostsValue, "ORDER", order, "SYSTEM CURRENCY AMOUNT", systemCurrencyAmount);
                    requestData.shippingCostsValue = shippingCostsValue;
                }

                // Determine the return costs value from the fieldReturnCosts setting based on the order's grossTotal
                if (includeReturnCosts || includeInternationalReturnCosts) {
                    const returnCostsValue = getReturnCostsValue();
                    if (returnCostsValue === null || returnCostsValue === undefined) {
                        await client.interface.trigger("showNotify", {
                            type: "danger",
                            message: t("return_costs_config_missing") || "No matching return cost bracket found for this order value. Please check the fieldReturnCosts setting."
                        });
                        setLoading(false);
                        return;
                    }
                    console.log("DEBUG - RETURN COSTS VALUE", returnCostsValue, "GROSS TOTAL", getOrderGrossTotal());
                    requestData.returnCostsValue = returnCostsValue;
                }
            }

            const response = await client.request.invoke(actionName, requestData);

            await client.interface.trigger("showNotify", { type: "success", message: t("order_created_successfully") });
            
            // Call the callback to handle the created order
            onCreateOrder(response.response);
            
            handleCloseModal();

        } catch (error) {
            console.error('Error creating order:', error);
            let errorMessage = error.message || t("something_went_wrong");
            
            // Handle validation errors
            if (error.validation_errors) {
                const validationMessages = Object.values(error.validation_errors).flat();
                errorMessage = validationMessages.join(', ');
            }
            
            await client.interface.trigger("showNotify", { type: "danger", message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    // Style the modal content
    useEffect(() => {
        setTimeout(() => {
            if (modalContentRef.current && modalContentRef.current.shadowRoot) {
                const shadowRoot = modalContentRef.current.shadowRoot;
                const style = document.createElement('style');
                style.textContent = '.content { padding-inline: 3px !important; }';
                shadowRoot.appendChild(style);
            }
        }, 100);
    }, []);

    return (
        <>
            <FwModal ref={modalRef} id='create-order-modal' size="large" titleText="" slider onFwClose={handleCloseModal} hideFooter="true">
                <FwModalContent ref={modalContentRef}>
                    <div>
                        <div className="text-lg font-bold mb-4">{t("create_new_order")}</div>
                        
                        {/* Order Type Selection */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t("select_order_type")}
                            </label>
                            <FwSelect
                                value={selectedOrderType}
                                onFwChange={handleOrderTypeChange}
                                placeholder={t("choose_order_type")}
                                state="normal"
                            >
                                {availableOrderTypes.map(type => (
                                    <FwSelectOption key={type.id} value={type.id.toString()}>
                                        {type.name}
                                    </FwSelectOption>
                                ))}
                            </FwSelect>
                        </div>

                        {/* Shipping and Return Costs Checkboxes (only for credit notes) */}
                        {selectedOrderType === '4' && (
                            <div className="mb-4 space-y-2">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={includeShippingCosts}
                                        onChange={(e) => setIncludeShippingCosts(e.target.checked)}
                                        className="mr-2"
                                    />
                                    <span className="text-sm font-medium text-gray-700">
                                        Versandkosten gutschreiben
                                    </span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={includeReturnCosts}
                                        onChange={(e) => {
                                            setIncludeReturnCosts(e.target.checked);
                                            if (e.target.checked) {
                                                setIncludeInternationalReturnCosts(false);
                                            }
                                        }}
                                        className="mr-2"
                                    />
                                    <span className="text-sm font-medium text-gray-700">
                                        Nationale Rücksendekosten hinzufügen
                                    </span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={includeInternationalReturnCosts}
                                        onChange={(e) => {
                                            setIncludeInternationalReturnCosts(e.target.checked);
                                            if (e.target.checked) {
                                                setIncludeReturnCosts(false);
                                            }
                                        }}
                                        className="mr-2"
                                    />
                                    <span className="text-sm font-medium text-gray-700">
                                        Internationale Rücksendekosten hinzufügen
                                    </span>
                                </label>
                            </div>
                        )}

                        {/* Items Selection */}
                        {selectedOrderType && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t("select_items")}
                                </label>
                                <div className="max-h-96 overflow-y-auto">
                                    {orderItems.map((orderItem, index) => {
                                        const selectedItem = selectedItems.find(item => item.orderItemId === orderItem.id);
                                        if (!selectedItem) return null;

                                        return (
                                            <div key={orderItem.id} className="border border-gray-200 rounded-lg p-4 mb-3 bg-gray-50">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div className="flex-1">
                                                        <h4 className="font-medium text-gray-900">{orderItem.orderItemName}</h4>
                                                        {orderItem.variation && (
                                                            <div className="text-sm text-gray-600 mt-1">
                                                                {orderItem.variation.number && (
                                                                    <span>VarNr: {orderItem.variation.number}</span>
                                                                )}
                                                                {orderItem.variation.id && (
                                                                    <span className="ml-3">ID: {orderItem.variation.id}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div className="text-sm text-gray-600 mt-1">
                                                            {t("original_quantity")}: {orderItem.quantity} | {t("price")}: {formatOrderItemAmount(orderItem)}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-3">
                                                    {/* Quantity Selection */}
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            {t("quantity")}
                                                        </label>
                                                                                                <FwInput
                                            type="number"
                                            value={selectedItem.quantity}
                                            min="0"
                                            max={selectedItem.maxQuantity}
                                            onFwInput={(e) => {
                                                e.stopPropagation();
                                                handleItemQuantityChange(orderItem.id, parseInt(e.detail.value) || 0);
                                            }}
                                            state="normal"
                                        />
                                                    </div>

                                                    {/* Return Reason (only for returns) */}
                                                    {selectedOrderType === '3' && (
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                                {t("return_reason")}
                                                            </label>
                                                                                                        <FwSelect
                                                value={selectedItem.returnReasonId.toString()}
                                                onFwChange={(e) => handleReturnReasonChange(e, orderItem.id)}
                                                state="normal"
                                            >
                                                                {returnReasons.map(reason => (
                                                                    <FwSelectOption key={reason.id} value={reason.id.toString()}>
                                                                        {reason.reason}
                                                                    </FwSelectOption>
                                                                ))}
                                                            </FwSelect>
                                                        </div>
                                                    )}

                                                    {/* Custom Price (only for credit notes) */}
                                                    {selectedOrderType === '4' && (
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                                {t("custom_price")}
                                                            </label>
                                                                                                        <FwInput
                                                type="number"
                                                value={selectedItem.customPrice}
                                                min="0"
                                                step="0.01"
                                                onFwInput={(e) => {
                                                    e.stopPropagation();
                                                    handleItemPriceChange(orderItem.id, parseFloat(e.detail.value) || 0);
                                                }}
                                                state="normal"
                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Credit Note Reason (only for credit notes) */}
                                                {selectedOrderType === '4' && (
                                                    <div className="mt-3">
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            {t("credit_note_reason")}
                                                        </label>
                                                        <FwSelect
                                                            value={selectedItem.creditNoteReasonId.toString()}
                                                            onFwChange={(e) => handleCreditNoteReasonChange(e, orderItem.id)}
                                                            state="normal"
                                                        >
                                                            {creditNoteReasons.map(reason => (
                                                                <FwSelectOption key={reason.id} value={reason.id.toString()}>
                                                                    {reason.reason}
                                                                </FwSelectOption>
                                                            ))}
                                                        </FwSelect>
                                                    </div>
                                                )}

                                                {/* Remaining Percentage (only for returns) */}
                                                {selectedOrderType === '3' && (
                                                    <div className="mt-3">
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            {t("remaining_percentage")}
                                                        </label>
                                                                                                <FwInput
                                            type="number"
                                            value={selectedItem.remainingPercentage}
                                            min="0"
                                            max="100"
                                            onFwInput={(e) => {
                                                e.stopPropagation();
                                                handleRemainingPercentageChange(orderItem.id, parseInt(e.detail.value) || 0);
                                            }}
                                            state="normal"
                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-between items-center mt-5">
                            <FwButton color="text" onClick={(e) => {
                                e.stopPropagation();
                                handleCloseModal();
                            }}>
                                {t("cancel")}
                            </FwButton>
                            <FwButton 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSubmit();
                                }}
                                disabled={loading || !selectedOrderType || selectedItems.filter(item => item.quantity > 0).length === 0}
                            >
                                {loading ? "..." : t("create_order")}
                            </FwButton>
                        </div>
                    </div>
                </FwModalContent>
            </FwModal>

            {/* Trigger Button */}
            <FwTooltip content={t('create_new_order')}>
            <div className="h-full">
                <button
                    onClick={handleOpenModal}
                    className="bg-slate-600 w-full hover:bg-slate-500 text-white font-bold py-2 px-3 rounded flex-grow flex items-center justify-center"
                    data-test-id="create-order-btn"
                >
                    <img src={iconCreateOrder} alt={t('create_new_order')} />
                </button>
                </div>
            </FwTooltip>
        </>
    );
};

export default CreateOrderModal; 